import { create } from 'zustand'
import { wheel500Api } from '../services/wheel500Api'
import useAppStore from '../../../store/useAppStore'

const PENDING_ITEM_LOCK_MS = 5000
let betFormRequestId = 0

const useWheel500Store = create((set, get) => ({
  activeRound: null,
  engine: null,
  isConnected: false,
  limits: null,
  isLoading: false,
  isBetting: false,
  error: null,
  inventory: [],
  balance: 0,
  pendingItemLocks: {},
  ownBetAcks: [],
  isModeDisabled: false,

  clearError: () => set({ error: null }),

  setModeDisabled: (value) => set({ isModeDisabled: Boolean(value) }),

  loadActiveRound: async (signal) => {
    try {
      set({ isLoading: true, error: null })
      const round = await wheel500Api.getActiveRound(signal)

      if (round?.error) {
        const error = new Error(round.error)
        error.message = round.error
        throw error
      }

      set((state) => state.engine === 'node' && state.isConnected
        ? { isLoading: false }
        : { activeRound: round ? { ...round, received_at: Date.now() } : round, engine: round?.engine || 'worker', isLoading: false, isModeDisabled: false })

      return round
    } catch (error) {
      if (signal?.aborted) {
        set({ isLoading: false })
        throw error
      }
      if (error?.code === 'mode_disabled' || error?.message === 'mode_disabled') {
        set((state) => state.engine === 'node' && state.isConnected
          ? { isLoading: false }
          : { isLoading: false, isModeDisabled: true, activeRound: null, error: null })
        return null
      }

      set({
        error: error.message || 'Ошибка загрузки раунда',
        isLoading: false
      })
      throw error
    }
  },

  loadBetForm: async (signal) => {
    const requestId = ++betFormRequestId
    try {
      set({ isLoading: true, error: null })
      const data = await wheel500Api.getBetForm(signal, () => get().engine === 'node')
      if (requestId !== betFormRequestId) return data

      if (data?.error) {
        const error = new Error(data.error)
        error.message = data.error
        throw error
      }

      set((state) => {
        const now = Date.now()
        const activeLocks = Object.fromEntries(
          Object.entries(state.pendingItemLocks || {}).filter(([, expiresAt]) => Number(expiresAt) > now)
        )

        const filteredInventory = (data.inventory || []).filter((item) => !activeLocks[item.id])

        return {
          inventory: filteredInventory,
          balance: state.engine === 'node' ? state.balance : Number(data.balance || 0),
          pendingItemLocks: activeLocks,
          isLoading: false
        }
      })

      return data
    } catch (error) {
      if (requestId !== betFormRequestId) throw error
      set({
        error: error.message || 'Инвентарь недоступен',
        isLoading: false
      })
      throw error
    }
  },

  placeBet: async ({ roundId, zone, crystal, items }) => {
    try {
      set({ isBetting: true, error: null })

      const response = await wheel500Api.placeBet({
        roundId,
        zone,
        crystal,
        items
      })

      set((state) => {
        const selectedItemIds = new Set(Array.isArray(items) ? items : [])
        const nextInventory = (state.inventory || []).filter((item) => !selectedItemIds.has(item.id))
        const crystalValue = Number(crystal || 0)
        const currentBalance = Number(state.balance || 0)
        const lockExpiresAt = Date.now() + PENDING_ITEM_LOCK_MS
        const nextPendingItemLocks = { ...state.pendingItemLocks }

        selectedItemIds.forEach((itemId) => {
          nextPendingItemLocks[itemId] = lockExpiresAt
        })

        return {
          isBetting: false,
          inventory: nextInventory,
          balance: Math.max(0, currentBalance - (Number.isFinite(crystalValue) ? crystalValue : 0)),
          pendingItemLocks: nextPendingItemLocks
        }
      })

      return response
    } catch (error) {
      set({
        error: error.message || 'Ошибка отправки ставки',
        isBetting: false
      })
      throw error
    }
  },

  loadHistoryFast: async () => {
    try {
      set({ isLoading: true, error: null })
      const data = await wheel500Api.getHistoryFast()

      if (data?.error) {
        const error = new Error(data.error)
        error.message = data.error
        throw error
      }

      set({ isLoading: false })
      return {
        rounds: Array.isArray(data?.rounds) ? data.rounds : [],
        myBets: Array.isArray(data?.my_bets) ? data.my_bets : []
      }
    } catch (error) {
      set({
        error: error.message || 'Ошибка загрузки истории',
        isLoading: false
      })
      throw error
    }
  },

  loadHistory: async () => {
    try {
      set({ isLoading: true, error: null })
      const [all, mine] = await Promise.all([
        wheel500Api.getHistoryAll(),
        useAppStore.getState().isGuest ? Promise.resolve([]) : wheel500Api.getHistoryMine()
      ])

      set({ isLoading: false })
      return {
        all: Array.isArray(all) ? all : [],
        mine: Array.isArray(mine) ? mine : []
      }
    } catch (error) {
      set({
        error: error.message || 'Ошибка загрузки истории',
        isLoading: false
      })
      throw error
    }
  },

  loadRoundById: async (roundId) => {
    try {
      set({ isLoading: true, error: null })
      const round = await wheel500Api.getRoundById(roundId)
      set({ isLoading: false })
      return round
    } catch (error) {
      set({
        error: error.message || 'Ошибка загрузки раунда',
        isLoading: false
      })
      throw error
    }
  }
}))

export default useWheel500Store
